import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, ListTodo } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import TaskTable from "@/components/TaskTable";
import TaskFormDialog from "@/components/TaskFormDialog";

const STATUSES = ["Open", "In Progress", "Completed", "Cancelled"];
const PRIORITIES = ["High", "Medium", "Low"];
const LINK_TYPES = ["Asset", "Lead", "Broker"];

function Toggle({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
        active ? "bg-ink text-white border-ink" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
      }`}
    >
      {label}
    </button>
  );
}

export default function TasksList() {
  const { user } = useAuth();
  const { id: routeTaskId } = useParams();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || (user?.roles || []).includes("admin");

  const [statusFilter, setStatusFilter] = useState(["Open", "In Progress"]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [assignee, setAssignee] = useState("all");
  const [linkType, setLinkType] = useState("all");
  const [dueFrom, setDueFrom] = useState(null);
  const [dueTo, setDueTo] = useState(null);
  const [fromNotes, setFromNotes] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);

  const { data: users = [] } = useQuery({
    queryKey: ["task-assignees"],
    queryFn: async () => (await api.get("/auth/users")).data,
    enabled: isAdmin,
  });

  const { data: tasks = [], refetch } = useQuery({
    queryKey: ["tasks", statusFilter, priorityFilter, assignee, linkType, dueFrom, dueTo, fromNotes],
    queryFn: async () => {
      const sp = new URLSearchParams();
      statusFilter.forEach((s) => sp.append("status", s));
      priorityFilter.forEach((p) => sp.append("priority", p));
      if (assignee !== "all") sp.append("assigned_to", assignee);
      if (linkType !== "all") sp.append("linked_record_type", linkType);
      if (dueFrom) sp.append("due_from", dueFrom);
      if (dueTo) sp.append("due_to", dueTo);
      if (fromNotes) sp.append("source", "notes");
      return (await api.get(`/tasks?${sp.toString()}`)).data;
    },
  });

  // Open edit dialog when navigated to /tasks/:id (e.g. from a reminder email)
  useEffect(() => {
    if (!routeTaskId) return;
    api.get(`/tasks/${routeTaskId}`).then((r) => { setEditTask(r.data); setFormOpen(true); }).catch(() => {});
  }, [routeTaskId]);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const openNew = () => { setEditTask(null); setFormOpen(true); };
  const openEdit = (t) => { setEditTask(t); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditTask(null); if (routeTaskId) navigate("/tasks", { replace: true }); };

  return (
    <div className="space-y-5" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ListTodo className="h-6 w-6 text-gold" /> Tasks
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Track actions across assets, leads and brokers.</p>
        </div>
        <Button onClick={openNew} data-testid="new-task-button">
          <Plus className="h-4 w-4 mr-1.5" /> New Task
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="label-eyebrow !text-slate-500 w-16">Status</span>
          {STATUSES.map((s) => (
            <Toggle key={s} label={s} active={statusFilter.includes(s)} onClick={() => toggle(statusFilter, setStatusFilter, s)} testid={`filter-status-${s.toLowerCase().replace(/\s+/g, "-")}`} />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="label-eyebrow !text-slate-500 w-16">Priority</span>
          {PRIORITIES.map((p) => (
            <Toggle key={p} label={p} active={priorityFilter.includes(p)} onClick={() => toggle(priorityFilter, setPriorityFilter, p)} testid={`filter-priority-${p.toLowerCase()}`} />
          ))}
          <span className="mx-1 h-4 w-px bg-slate-200" aria-hidden />
          <Toggle label="📝 From Notes" active={fromNotes} onClick={() => setFromNotes((v) => !v)} testid="filter-from-notes" />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {isAdmin && (
            <div className="w-44">
              <span className="label-eyebrow !text-slate-500 block mb-1">Assigned To</span>
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger data-testid="filter-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="w-40">
            <span className="label-eyebrow !text-slate-500 block mb-1">Linked Type</span>
            <Select value={linkType} onValueChange={setLinkType}>
              <SelectTrigger data-testid="filter-link-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any type</SelectItem>
                {LINK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <span className="label-eyebrow !text-slate-500 block mb-1">Due From</span>
            <DatePicker value={dueFrom} onChange={setDueFrom} testId="filter-due-from" placeholder="Any" />
          </div>
          <div className="w-40">
            <span className="label-eyebrow !text-slate-500 block mb-1">Due To</span>
            <DatePicker value={dueTo} onChange={setDueTo} testId="filter-due-to" placeholder="Any" />
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <TaskTable tasks={tasks} onEdit={openEdit} emptyText={fromNotes ? "No tasks from notes yet. Use Convert to Task on any note to create one." : "No tasks match your filters."} />
      </div>

      <TaskFormDialog open={formOpen} onClose={closeForm} task={editTask} />
    </div>
  );
}
